import React from "react";
import { DynamicText } from "@tennr/frontend/components/DynamicText";
import { MetadataTags } from "@tennr/frontend/components/MetadataTags";
import "./PrivacyPage.css";
import { HeroSection } from "@tennr/frontend/sections/HeroSection";
import { SiteFooter } from "@tennr/frontend/components/SiteFooter";
import { PageBackground } from "@tennr/frontend/components/PageBackground";

export const PrivacyPage: React.FC = () => {
  return (
    <>
      <MetadataTags
        title="Privacy Policy | Tennr"
        description="Tennr's privacy policy—learn how we collect, use, and protect your data"
        imageUrl="https://tennr.com/share_cards/sharecard-home.webp"
      />
      <PageBackground bgImage="/img/privacy-hero-bg.jpg" bgImageVert="/img/privacy-hero-bg-vert.jpg" />
      <main className="privacy-page">
        <HeroSection className="bottom-left"
          noAnimate={true}
          cmsContent={{
            heading: {
              heading: "Privacy Policy"
            }
          }}
        >
          <div>Tennr Privacy Policy<br/> Last Updated June 15,2025</div>
        </HeroSection>
        <DynamicText as="div" className="privacy-page-container">
          <PrivacyPageContent />
        </DynamicText>
        <SiteFooter taglinePosition="left" bgImage="/img/footer-demo-bg.jpg" bgImageVert="/img/footer-demo-bg-vert.jpg" />
        
      </main>
    </>
  );
};

const PrivacyPageContent: React.FC = () => {
  return (
    <>
      <p>
        Tennr was created to help health care providers improve patient care and less time on the administrative burden
        of modern health care operations. This Tennr Privacy Policy ("Privacy Policy") governs any personal information
        Tennr Incorporated ("Tennr", the "Company," "we," "us," or "our") collects, how we use and share that data, and
        your choices concerning our data practices.
      </p>

      <p>
        Our goal with this Privacy Policy is to inform you about how Tennr works by explaining the data you provide,
        data we collect, how we use it to provide the Services, and when that data is shared. We provide artificial
        intelligence-powered document processing and business process automation services through our website (the
        "Site") and our software services (such information, together with the Site, the "Services").
      </p>

      <p>
        The Services are directed towards our health care provider customers. The Services should not be used for
        personal, family, or household purposes, other than as described below. Before using the Services or submitting
        any personal information to us, please review this Privacy Policy carefully and contact us if you have any
        questions. By using the Services, you agree to the practices we describe in this Privacy Policy. If you do not
        agree to this Privacy Policy, please do not provide us with your personal information through the Site or
        otherwise use the Services.
      </p>
      <p>&nbsp; </p>
      <h2>1. Personal information we collect</h2>
      <h3>Personal Information You Provide Us</h3>
      <p>
        We collect personal information from you when you request a demo, participate in one of our events or
        conferences, contact us, visit the Site, our branded social media pages, or our offices, use the Service, apply
        for jobs, send communications to or receive communications from us, or otherwise provide personal information to
        us. The personal information we collect varies depending on what you choose to share, but it may include your
        name, phone number, mailing address, email address, job title, company name, and billing address.
      </p>

      <p>
        If you are a patient of one of our health care provider customers and you communicate with us via text message
        for your appointment scheduling needs, we collect your responses regarding appointment details and related
        scheduling information on behalf of our health care provider customers.
      </p>

      <p>
        We also have pages on social media sites such as YouTube, LinkedIn, and other third party platforms ("Social
        Media Pages"). When you interact with those Social Media Pages, the platform provider's privacy policy will
        apply to your interactions and their collection, use and processing of your personal information. You or the
        platforms may provide us with personal information through the platform, and we will treat the personal
        information we receive in accordance with this Privacy Policy.
      </p>
      <h3>Internet Activity Data</h3>
      <p>
        When you visit, use, and interact with the Services, we may receive certain personal information about your
        visit, use, or interactions. For example, we may monitor the number of people that visit the Services, peak
        hours of visits, which page(s) are visited, the domains our visitors come from (e.g., google.com, yahoo.com,
        etc.), and which browsers people use to access the Services (e.g., Chrome, Firefox, Microsoft Internet Explorer,
        etc.), broad geographical information, and navigation pattern. In particular, the following information is
        created and automatically logged in our systems:
      </p>

      <ul>
        <li>
          Log Data: Information that your browser automatically sends whenever you visit the Site. Log Data includes
          your Internet Protocol address, browser type and settings, the date and time of your request, and how you
          interacted with the Site.
        </li>
        <li>Cookies Data: Please see the "Cookies" section below to learn more about how we use cookies.</li>
        <li>
          Device Data: Includes name of the device, operating system, and browser you are using. Information collected
          may depend on the type of device you use and its settings.
        </li>
        <li>
          Usage Data: We collect information about how you use our Services, such as the types of content that you view
          or engage with, the features you use, the actions you take, and the time, frequency, and duration of your
          activities.
        </li>
        <li>Location Data: We derive a rough estimate of your location from your IP address.</li>
      </ul>
      <h3>Personal Information We Process on Behalf of Our Health Care Provider Customers</h3>
      <h4>Health Data</h4>
      <p>
        In order to provide the Services, our health care Provider customers share patient information with us through
        commonly-used forms in the health care industry. These forms include patient intake forms, referral forms,
        insurance forms, and other related health care documentation, which we process on their behalf for the purposes
        of automating administrative processes and appointment scheduling so that they can serve their patients' health
        care needs better. We process health data that we are provided pursuant to our agreements with our health care
        Provider customers and we apply HIPAA privacy and security standards to all health data that we collect.
      </p>
      <h4>De-Identified Data</h4>
      <p>
        De-identified data is non-personal information that is aggregated or de-identified so that it cannot reasonably
        be used to identify an individual. We use de-identified data for research and development of new products or
        tools, to refine our algorithms and machine learning applications, and to improve the Services. We may disclose
        such information publicly and to third parties, for example, in public reports about health, to partners under
        agreement with us, or in benchmarking information we provide to our health care provider customers.
      </p>

      <p>
        If you are a patient of one of our health care provider customers and have questions about the processing of
        your health data, please contact us by following the instructions set forth in "Privacy Rights" below.
      </p>
      <h4>Cookies</h4>
      <p>
        We use cookies to operate and administer our Site, gather usage data on our Site, and improve your experience on
        it.
      </p>

      <p>
        A "cookie" is a piece of information sent to your browser by a website you visit. Cookies can be stored on your
        computer for different periods of time. Some cookies expire after a certain amount of time, or upon logging out
        (session cookies), others survive after your browser is closed until a defined expiration date set in the cookie
        (as determined by the third party placing it), and help recognize your computer when you open your browser and
        browse the Internet again (persistent cookies). For more details on cookies please visit All About Cookies.
      </p>
    </>
  );
};
